import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import CardsAPI from 'custom/dashboard/api/cards';

// One task as the server has it, worked on its own: the task bubble on a conversation edits the
// task it announces without loading its card. Every change shows the task the server answered
// with.
export function useCardTask() {
  const { t } = useI18n();
  const task = ref(null);

  // Leaves `task` empty when the task cannot be read (e.g. a card the agent cannot see).
  const load = async (cardId, taskId) => {
    try {
      const { data } = await CardsAPI.getTask(cardId, taskId);
      task.value = data;
    } catch {
      task.value = null;
    }
  };

  // `target` is the task itself or the visit it carries (see visitTask.js). A change to the visit
  // answers with the visit, so the task is read again to show it.
  const apply = async (target, request) => {
    try {
      const { data } = await request();
      if (target.id === task.value.id) task.value = data;
      else await load(task.value.card_id, task.value.id);
    } catch {
      useAlert(t('CARDS.TASKS.UPDATE_ERROR'));
    }
  };

  const update = (attributes, target = task.value) =>
    apply(target, () =>
      CardsAPI.updateTask(target.card_id, target.id, attributes)
    );

  const changeStatus = (transition, target = task.value) =>
    apply(target, () =>
      CardsAPI.changeTaskStatus(target.card_id, target.id, transition)
    );

  // `visit` is { scheduledAt, assigneeId }. Resolves to whether it was scheduled, so a dialog only
  // closes on success.
  const scheduleVisit = async visit => {
    try {
      const { data } = await CardsAPI.scheduleVisit(
        task.value.card_id,
        task.value.id,
        visit
      );
      task.value = data;
      return true;
    } catch {
      useAlert(t('CARDS.TASKS.UPDATE_ERROR'));
      return false;
    }
  };

  return { task, load, update, changeStatus, scheduleVisit };
}
